import React, { useEffect, useRef } from "react";
import "./kit.css";

// Shared parts of the redesigned admin screens (Notification Centre redesign, R1.1; see the R0 mockups'
// "Look and components"). Every new screen builds from these instead of its own copies.

// ---------------------------------- status chip ----------------------------------
// One meaning per tone, everywhere: ok (live / approved / ready), wait (waiting / in review / fallback),
// bad (urgent / paused / poor), info (info / library template), own (your wording / private),
// neutral (built-in / off / draft).
export const StatusChip = ({ tone = "neutral", children, title }) => <span className={`kit-chip kit-chip-${tone}`} title={title}>{children}</span>;

// ---------------------------------- side panel ----------------------------------
// Opens over the right side of a list and keeps the list in view; Esc or × closes it.
export const SidePanel = ({ open, title, subtitle, onClose, children, footer, width = 440, label }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        ref.current?.focus();
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);
    if (!open) return null;
    return (
        <aside className="kit-panel" style={{ width: `min(${width}px, 100%)` }} aria-label={label || title} tabIndex={-1} ref={ref}>
            <div className="kit-panel-head">
                <div className="kit-panel-titles">
                    {subtitle ? <div className="kit-sub">{subtitle}</div> : null}
                    <h2 className="kit-panel-title">{title}</h2>
                </div>
                <button type="button" className="kit-icon-btn" onClick={onClose} aria-label="Close">×</button>
            </div>
            <div className="kit-panel-body">{children}</div>
            {footer ? <div className="kit-panel-foot">{footer}</div> : null}
        </aside>
    );
};

// ---------------------------------- empty state ----------------------------------
export const EmptyState = ({ title, text, action }) => (
    <div className="kit-empty">
        <div className="kit-empty-title">{title}</div>
        {text ? <div className="kit-sub">{text}</div> : null}
        {action ? <div className="kit-empty-action">{action}</div> : null}
    </div>
);

// ---------------------------------- save bar ----------------------------------
// Sticks to the bottom while there are unsaved changes.
export const SaveBar = ({ show, onSave, onDiscard, saving, text = "You have unsaved changes" }) => (show ? (
    <div className="kit-savebar" role="region" aria-label="Unsaved changes">
        <span className="kit-savebar-text">{text}</span>
        {onDiscard ? <button type="button" className="kit-btn kit-btn-dark-outline" onClick={onDiscard} disabled={saving}>Discard</button> : null}
        <button type="button" className="kit-btn kit-btn-light" onClick={onSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
    </div>
) : null);

// ---------------------------------- confirm dialog ----------------------------------
export const ConfirmDialog = ({ open, title, text, confirmLabel = "Confirm", danger, busy, onConfirm, onCancel }) => {
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onCancel?.(); };
        document.addEventListener("keydown", onKey);
        ref.current?.focus();
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onCancel]);
    if (!open) return null;
    return (
        <div className="kit-backdrop">
            <div className="kit-dialog" role="dialog" aria-modal="true" aria-labelledby="kit-dialog-title">
                <h2 id="kit-dialog-title" className="kit-dialog-title">{title}</h2>
                {text ? <div className="kit-dialog-text">{text}</div> : null}
                <div className="kit-dialog-actions">
                    <button type="button" className="kit-btn kit-btn-outline" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button type="button" ref={ref} className={`kit-btn ${danger ? "kit-btn-danger" : "kit-btn-primary"}`} onClick={onConfirm} disabled={busy}>{busy ? "Working..." : confirmLabel}</button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------- preview frames ----------------------------------
// One per channel, used by every editor. They show text as given (fill the sample values first).

export const PushPreview = ({ title, body, image, appName = "GetMyDrive", platform = "android" }) => (
    platform === "web" ? (
        <div className="kit-pv-web">
            {image ? <img src={image} alt="" className="kit-pv-web-img" /> : null}
            <div className="kit-pv-web-text"><div className="kit-pv-strong">{title || "-"}</div><div>{body}</div><div className="kit-pv-meta">getmydrive.in</div></div>
        </div>
    ) : (
        <div className="kit-pv-android">
            <div className="kit-pv-android-head"><span className="kit-pv-app-dot" />{appName} · now</div>
            <div className="kit-pv-strong">{title || "-"}</div>
            <div className="kit-pv-android-body">{body}</div>
            {image ? <img src={image} alt="" className="kit-pv-android-img" /> : null}
        </div>
    )
);

// header: { type: "text" | "image" | "video" | "document" | "location", text, image, fileName, place: { name, address } }
// buttons: [{ text, icon }]
export const WhatsAppPreview = ({ header, body, footer, buttons = [], time = "15:02" }) => {
    const shown = buttons.length > 3 ? [...buttons.slice(0, 2), { text: "See all options", icon: "☰" }] : buttons;
    return (
        <div className="kit-pv-wa">
            <div className="kit-pv-wa-bubble">
                {header?.type === "image" ? (header.image ? <img src={header.image} alt="" className="kit-pv-wa-media" /> : <div className="kit-pv-wa-media kit-pv-wa-ph">Image</div>) : null}
                {header?.type === "video" ? <div className="kit-pv-wa-media kit-pv-wa-video" aria-label="Video"><span>▶</span></div> : null}
                {header?.type === "document" ? <div className="kit-pv-wa-doc"><span className="kit-pv-wa-pdf">PDF</span><span>{header.fileName || "Document.pdf"}</span></div> : null}
                {header?.type === "location" ? <div className="kit-pv-wa-loc"><div className="kit-pv-wa-map" aria-hidden="true">📍</div><div className="kit-pv-wa-place"><strong>{header.place?.name || "Location"}</strong><br />{header.place?.address || ""}</div></div> : null}
                {header?.type === "text" && header.text ? <div className="kit-pv-wa-head">{header.text}</div> : null}
                <div className="kit-pv-wa-body">{body || " "}</div>
                {footer ? <div className="kit-pv-wa-foot">{footer}</div> : null}
                <div className="kit-pv-wa-time">{time}</div>
                {shown.map((b, i) => <div key={i} className="kit-pv-wa-btn">{b.icon ? <span aria-hidden="true">{b.icon}</span> : null}{b.text || "Button"}</div>)}
            </div>
        </div>
    );
};

// html: the email's HTML (shown sandboxed). width: 600 (desktop) or 360 (phone).
export const EmailPreview = ({ subject, preheader, html, width = 600, from = "GetMyDrive" }) => (
    <div className="kit-pv-email">
        <div className="kit-pv-email-inbox"><div className="kit-pv-strong">{subject || "-"}</div><div className="kit-sub">{from}{preheader ? ` · ${preheader}` : ""}</div></div>
        <iframe title="Email preview" sandbox="" className="kit-pv-email-frame" style={{ width: `${width}px`, maxWidth: "100%" }} srcDoc={`<!doctype html><html><body style="margin:0;background:#F4F1F6;">${html || ""}</body></html>`} />
    </div>
);

export const SmsPreview = ({ sender = "GTMDRV", text }) => (
    <div className="kit-pv-sms"><div className="kit-pv-meta" style={{ textAlign: "center" }}>{sender}</div><div className="kit-pv-sms-bubble">{text || " "}</div></div>
);
